"use client";

import { handleBlogScroll } from "@/app/utils/scroll";
import WaitlistInput from "@/components/WaitlistInput";

const bgColor = "bg-[#FAFAFA]";

export const tendlc_nightmare_why_its_taking_weeks_to_get_approved = (
  <>
    <p>
      The era of instant SMS is officially over for developers. Just a few years
      ago, you could buy a phone number and start sending text messages to your
      users in minutes. Today, the "Wild West" of messaging has been replaced by
      a dense wall of bureaucracy known as 10DLC. If you are trying to launch a
      new app that relies on text notifications, you are likely finding yourself
      stuck in a "pending" status that seems to last forever.
    </p>
    <p>
      The frustration is real because the registration process has become a
      major bottleneck for product launches. You might have the perfect code and
      a beautiful interface, but if your SMS traffic isn't verified, your
      messages simply won't be delivered. Understanding why this process takes
      weeks is the first step toward navigating the complexity and finally
      getting your messages into your users' pockets.
    </p>

    <div className="w-full flex flex-col gap-y-4 rounded-xl h-auto p-8 bg-blue-100 text-gray-500">
      <p className="ml-4">
        1.{" "}
        <button
          onClick={(e) => {
            e.preventDefault();
            handleBlogScroll("what-exactly-is-the-10dlc-system?");
          }}
          className="text-primary hover:text-secondary underline text-left"
        >
          What Exactly is the 10DLC System?
        </button>
      </p>
      <p className="ml-4">
        2.{" "}
        <button
          onClick={(e) => {
            e.preventDefault();
            handleBlogScroll("the-bottleneck-of-manual-vetting");
          }}
          className="text-primary hover:text-secondary underline text-left"
        >
          The Bottleneck of Manual Vetting
        </button>
      </p>
      <p className="ml-4">
        3.{" "}
        <button
          onClick={(e) => {
            e.preventDefault();
            handleBlogScroll("the-website-compliance-trap");
          }}
          className="text-primary hover:text-secondary underline text-left"
        >
          The Website Compliance Trap
        </button>
      </p>
      <p className="ml-4">
        4.{" "}
        <button
          onClick={(e) => {
            e.preventDefault();
            handleBlogScroll("the-mystery-of-campaign-use-cases");
          }}
          className="text-primary hover:text-secondary underline text-left"
        >
          The Mystery of Campaign Use Cases
        </button>
      </p>
      <p className="ml-4">
        5.{" "}
        <button
          onClick={(e) => {
            e.preventDefault();
            handleBlogScroll("hidden-costs-and-business-risks");
          }}
          className="text-primary hover:text-secondary underline text-left"
        >
          Hidden Costs and Business Risks
        </button>
      </p>
      <p className="ml-4">
        6.{" "}
        <button
          onClick={(e) => {
            e.preventDefault();
            handleBlogScroll("shifting-regulations-and-moving-goalposts");
          }}
          className="text-primary hover:text-secondary underline text-left"
        >
          Shifting Regulations and Moving Goalposts
        </button>
      </p>
      <p className="ml-4">
        7.{" "}
        <button
          onClick={(e) => {
            e.preventDefault();
            handleBlogScroll("simplifying-the-registration-struggle");
          }}
          className="text-primary hover:text-secondary underline text-left"
        >
          Simplifying the Registration Struggle
        </button>
      </p>
    </div>

    <h2
      id="what-exactly-is-the-10dlc-system?"
      className="text-3xl font-bold mt-4"
    >
      What Exactly is the 10DLC System?
    </h2>
    <p>
      10DLC stands for 10-Digit Long Code, and it is a system created by major
      mobile carriers to verify the identity of businesses sending messages. The
      goal is to eliminate spam by ensuring that every sender is a legitimate
      entity. While this is great for the average consumer's inbox, it has
      created a massive amount of paperwork and technical verification for
      developers.
    </p>
    <p>
      The system requires you to register your "Brand," which is your legal
      business identity, and your "Campaign," which is the specific reason you
      are sending texts. Each of these layers must be reviewed and approved by
      third party aggregators before the carriers allow your traffic through.
      This multi layered approach is designed to be thorough, but the
      thoroughness is exactly what causes the significant delays.
    </p>
    <h2
      id="the-bottleneck-of-manual-vetting"
      className="text-3xl font-bold mt-4"
    >
      The Bottleneck of Manual Vetting
    </h2>
    <p>
      The primary reason for the weeks of waiting is that human beings are often
      the ones reviewing these applications. There has been a massive surge in
      10DLC registrations as carriers have tightened their filters, completely
      overwhelming the staff at the vetting agencies. When thousands of
      businesses apply at the same time, a massive queue forms that can take
      weeks to clear.
    </p>
    <p>
      Reviewers are incredibly meticulous because they are held accountable by
      the carriers. Even a tiny discrepancy, such as a slight variation between
      your legal business name and your tax ID records, can result in an
      immediate rejection. When you get rejected, you often have to start the
      entire process over, effectively putting you at the back of a very long
      line.
    </p>
    <h2 id="the-website-compliance-trap" className="text-3xl font-bold mt-4">
      The Website Compliance Trap
    </h2>
    <p>
      Carriers do not just look at your application; they also look at your
      digital presence to ensure you are a real business. If your website does
      not have a clearly visible privacy policy or a transparent SMS opt in
      method, your 10DLC application will likely be denied. Many developers are
      surprised to find that their general website privacy policy is
      insufficient for SMS standards.
    </p>
    <p>
      You must include specific language stating that mobile information will
      not be shared with third parties for marketing purposes. Furthermore, your
      opt in forms must clearly explain exactly what the user is signing up for
      and how they can stop receiving messages. If these elements are missing or
      hidden on a subpage, the vetting agents will flag your brand as non
      compliant and halt your approval.
    </p>
    <h2
      id="the-mystery-of-campaign-use-cases"
      className="text-3xl font-bold mt-4"
    >
      The Mystery of Campaign Use Cases
    </h2>
    <p>
      Describing your "Campaign" is often where developers face the most
      trouble. You have to provide a detailed explanation of why you are
      messaging users and provide exact templates of the messages you plan to
      send. If your description is too vague, like simply saying "sending
      notifications," the carriers will assume you are a high risk sender and
      deny the request.
    </p>
    <p>
      The templates must match the actual traffic you intend to send almost
      perfectly. If the vetting agent sees a template for a security code but
      you later send marketing blasts, your account can be suspended. This level
      of scrutiny ensures that businesses are being honest about their intent,
      but it requires a level of planning that most developers find tedious and
      time consuming.
    </p>
    <h2
      id="hidden-costs-and-business-risks"
      className="text-3xl font-bold mt-4"
    >
      Hidden Costs and Business Risks
    </h2>
    <p>
      The pain of the 10DLC nightmare isn't just the lost time; it is the
      financial impact on your business. Most providers charge registration fees
      and monthly campaign fees regardless of whether your traffic is actually
      approved or flowing. This means you could be paying for a service for an
      entire month before you are even allowed to send your first message.
    </p>
    <p>
      For a startup, these delays can be catastrophic for a launch timeline. If
      your user experience depends on a "magic link" or a two factor
      authentication code sent via SMS, your app is effectively broken until the
      carriers give you the green light. This risk makes the SMS portion of your
      infrastructure one of the most volatile components of your entire tech
      stack.
    </p>
    <h2
      id="shifting-regulations-and-moving-goalposts"
      className="text-3xl font-bold mt-4"
    >
      Shifting Regulations and Moving Goalposts
    </h2>
    <p>
      The rules for 10DLC are not set in stone and frequently change without
      much warning to the developer community. What was considered a compliant
      privacy policy last month might be rejected this month due to a new
      carrier update. Keeping up with these shifting requirements is a full time
      job that takes you away from actually building your product.
    </p>
    <p>
      Because the carriers prioritize network security over developer
      convenience, they have no problem adding new requirements mid stream. This
      constant state of flux means that even if you follow every guide
      perfectly, there is still a chance of a "blindside" rejection. Navigating
      this bureaucracy is a specialized skill that most engineering teams simply
      don't have the time to master.
    </p>
    <h2
      id="simplifying-the-registration-struggle"
      className="text-3xl font-bold mt-4"
    >
      Simplifying the Registration Struggle
    </h2>
    <p>
      While you cannot avoid the 10DLC rules if you want to use local numbers,
      you can change how you approach the problem. The key is to stop treating
      SMS as a simple API call and start treating it as a complex regulatory
      challenge. Proper preparation of your legal documents and website
      compliance before you even hit the submit button can save you weeks of
      back and forth.
    </p>
    <p>
      Most developers are now looking for ways to abstract this headache away so
      they can focus on their core features instead of carrier politics. The
      best way to ensure your messages get delivered without getting stuck in
      the manual paperwork loop is to find a partner that understands the
      nuances of the registration system. You can avoid the manual 10DLC
      nightmare by using NotifyAPI to handle this.
    </p>
    <WaitlistInput />
  </>
);
